import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { childrenInstanceOf } from 'bloko/common/helpers/customPropTypes';

import Modal, { ModalHeader, ModalTitle, ModalFooter, ModalError } from 'bloko/blocks/modal';
import Button from 'bloko/blocks/button';
import Input from 'bloko/blocks/input';
import Loading from 'bloko/blocks/loading';
import TreeSelector, { TreeSelectorDummy } from 'bloko/blocks/treeSelector';

import 'bloko/blocks/treeSelectorPopup/treeSelectorPopup.less';

export const dataQa = {
    submit: 'bloko-tree-selector-popup-submit',
    cancel: 'bloko-tree-selector-popup-cancel',
    search: 'bloko-tree-selector-popup-search',
    notFound: 'bloko-tree-selector-popup-not-found',
};

const isEqual = (firstArray, secondArray) => {
    const firstArrayLength = firstArray.length;
    const secondArrayLength = secondArray.length;

    if (secondArrayLength !== firstArrayLength) {
        return false;
    }

    for (let i = 0; i <= firstArrayLength; i++) {
        if (firstArray[i] !== secondArray[i]) {
            return false;
        }
    }

    return true;
};

/**
 * Компонент для работы с древовидной структурой данных с возможностью поиска. Открывается в модальном окне.
 */
export default class TreeSelectorPopup extends PureComponent {
    static propTypes = {
        /** Компонент TreeSelector */
        children: childrenInstanceOf([TreeSelector, TreeSelectorDummy]),
        /** Показывать ли TreeSelectorPopup */
        visible: PropTypes.bool,
        /** Заголовок */
        title: PropTypes.node,
        /** Отображать ли строку поиска */
        search: PropTypes.bool,
        /** Закрывать ли попап по кнопке подтверждения */
        hideOnSubmitClick: PropTypes.bool,
        /** Переводы:<br/>
         * `submit` - кнопка подтвержения<br/>
         * `cancel` - кнопка отмены<br/>
         * `searchPlaceholder` - плейсхолдер в строке поиска<br/>
         * `notFound` - сообщение о том, что по поисковому запросу ничего не найдено */
        trl: PropTypes.shape({
            submit: PropTypes.string.isRequired,
            cancel: PropTypes.string.isRequired,
            searchPlaceholder: PropTypes.string,
            notFound: PropTypes.string,
        }).isRequired,
        /** Подсказка над TreeSelector */
        hint: PropTypes.node,
        /** Текст ошибки */
        error: PropTypes.string,
        /** Заблокирована ли кнопка подтверждения */
        disabledSubmit: PropTypes.bool,
        /** Отображать ли иконку загрузки на кнопке подтверждения */
        showLoadingOnSubmit: PropTypes.bool,
        /** Обработчик изменения набора выбранных ID.
         * В аргументах получает список выбранных элементов`{Array} selected`. */
        onChange: PropTypes.func,
        /** Обработчик закрытия TreeSelectorPopup */
        onClose: PropTypes.func.isRequired,
        /** Кастомный обработчик нажатия кнопки подтверждения
         * В аргументах получает список выбранных элементов `{Array} selected`. */
        onSubmit: PropTypes.func,
        /** Список выбранных id для контолируемого компонента */
        selected: PropTypes.array,
    };

    static defaultProps = {
        visible: false,
        search: true,
        hideOnSubmitClick: true,
        disabledSubmit: false,
        showLoadingOnSubmit: false,
        onChange: () => {},
    };

    isControlled = this.props.children.type === TreeSelectorDummy;
    treeSelectorProps = this.props.children.props;
    currentSelected = this.treeSelectorProps.initialSelected;
    currentExpanded = this.treeSelectorProps.initialExpanded;

    state = {
        contentFilterQuery: '',
        notFound: false,
        selected: this.isControlled ? this.props.selected : this.treeSelectorProps.initialSelected,
        expanded: this.treeSelectorProps.initialExpanded,
    };

    handleSearch = (event) => {
        this.setState({ contentFilterQuery: event.target.value || '' });
    };

    handleChangeFilterQuery = (visibleIds) => {
        this.setState({ notFound: !visibleIds.length });
    };

    handleSubmit = () => {
        const { onChange, onClose, onSubmit, hideOnSubmitClick } = this.props;
        const selected = this.isControlled ? this.props.selected : this.currentSelected;
        const expanded = this.currentExpanded;

        if (onSubmit) {
            onSubmit(selected.slice());
        } else if (!isEqual(this.state.selected, selected)) {
            this.setState({
                selected,
            });
            onChange(selected.slice());
        }

        if (!isEqual(this.state.expanded, expanded)) {
            this.setState({
                expanded,
            });
        }

        if (hideOnSubmitClick) {
            onClose();
        }
    };

    handleTreeSelectorChange = (id, isSelected, selected) => {
        if (this.isControlled) {
            this.props.children.props.onChange(id, isSelected);
        } else {
            this.currentSelected = selected;
        }
    };

    handleTreeSelectorExpand = (expanded) => {
        this.currentExpanded = expanded;
    };

    renderHeader = () => {
        const { title, search, hint, trl } = this.props;
        const { contentFilterQuery } = this.state;

        if (!title && !search && !hint) {
            return null;
        }

        return (
            <ModalHeader>
                <ModalTitle>{title}</ModalTitle>
                {search && (
                    <div className="bloko-tree-selector-popup-search">
                        <Input
                            type="search"
                            data-qa={dataQa.search}
                            placeholder={trl.searchPlaceholder}
                            value={contentFilterQuery}
                            onChange={this.handleSearch}
                        />
                    </div>
                )}
                {hint && <div className="bloko-tree-selector-popup-hint">{hint}</div>}
            </ModalHeader>
        );
    };

    renderTreeSelector = () => {
        const { trl } = this.props;
        const { contentFilterQuery, selected, expanded, notFound } = this.state;

        return (
            <div className="bloko-tree-selector-popup">
                {notFound && <div data-qa={dataQa.notFound}>{trl.notFound}</div>}
                <div className="bloko-tree-selector-popup-content">
                    {React.cloneElement(this.props.children, {
                        onChange: this.handleTreeSelectorChange,
                        onExpand: this.handleTreeSelectorExpand,
                        initialSelected: selected,
                        initialExpanded: expanded,
                        selected: this.props.selected,
                        contentFilterQuery,
                        onChangeFilterQuery: this.handleChangeFilterQuery,
                    })}
                </div>
            </div>
        );
    };

    renderError = () => {
        const { error } = this.props;

        return <ModalError visible={!!error}>{error}</ModalError>;
    };

    renderFooter = () => {
        const { trl, disabledSubmit, showLoadingOnSubmit, onClose } = this.props;

        let loadingProp = {};
        if (showLoadingOnSubmit) {
            loadingProp = {
                loading: <Loading initial={Loading.kinds.inverted} />,
            };
        }

        return (
            <ModalFooter>
                <span className="bloko-form-spacer">
                    <Button data-qa={dataQa.cancel} onClick={onClose}>
                        {trl.cancel}
                    </Button>
                </span>
                <span className="bloko-form-spacer">
                    <Button
                        kind={Button.kinds.primary}
                        disabled={disabledSubmit}
                        onClick={this.handleSubmit}
                        data-qa={dataQa.submit}
                        {...loadingProp}
                    >
                        {trl.submit}
                    </Button>
                </span>
            </ModalFooter>
        );
    };

    render() {
        const { visible, onClose } = this.props;

        return (
            <Modal visible={visible} onClose={onClose}>
                {this.renderHeader()}
                {this.renderTreeSelector()}
                {this.renderError()}
                {this.renderFooter()}
            </Modal>
        );
    }
}
